import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'
import { QualityLevelController } from '../qualityLevels'
import { SpatialAudioManager } from '../SpatialAudioManager'
import { diagnoseStreamError } from '../streamError'
import type { QualityLevelInfo } from '../surfaceTypes'
import { attachMediaDiagnostics, type DiagnosticsHandle } from './mediaDiagnostics'
import { installAesInitSegmentIvFix } from './aesInitSegmentIv'

export const HLS_TYPE = 'application/x-mpegURL'

// A seek target is never allowed to land exactly on the duration — that ends
// playback instead of resuming it.
const SEEK_EPSILON = 0.25

// Longest the black source-swap cover may stay up waiting for the new stream's
// first composited frame. Generous enough that it never pre-empts a normal
// swap, short enough that a missing frame callback is a blink, not a hang.
const SOURCE_PIXELS_DEADLINE_MS = 3000

// How long to wait for a composited frame after the new source has decoded its
// first one. Long enough for a visible element's frame callback to win the
// race, short enough that a hidden one is not a visible stall.
const SOURCE_PIXELS_GRACE_MS = 250

// VHS's own default starting estimate (4.19 Mbps). Enough for the 1080p rung
// of a typical 360 ladder; the real measurement takes over once segments start
// arriving.
const INITIAL_BANDWIDTH_ESTIMATE = 4194304

// On Safari (macOS and everything on iOS) HLS plays through WebKit's native
// pipeline instead of VHS/MSE. Forcing VHS there (overrideNative) wedges the
// decoder: it appends 30s of perfectly good fMP4 into the SourceBuffers, the
// element decodes exactly one frame (loadeddata, videoWidth set), and then
// currentTime stays pinned at 0 with readyState stuck at HAVE_CURRENT_DATA —
// no error, no event, at every rendition from 1080p to 4K. VHS's own docs
// recommend against overrideNative on Safari, and every major player (hls.js,
// Shaka) defers to native HLS there. Native playback also keeps the WebGL
// sphere legal: crossOrigin=anonymous plus the CORS headers Bunny already
// sends (verified by the ?debug=true segment probes) keep the texture
// untainted. The trade-off: VHS's rendition ladder is gone on Safari, so the
// quality menu hides itself and native ABR drives rendition choice.
//
// IS_ANY_SAFARI alone is not enough: Chrome on iPhone (CriOS) sets IS_CHROME,
// which excludes it from IS_ANY_SAFARI — but every browser on iOS is a shell
// over the same WebKit media stack, and iOS 17.1+ even exposes
// ManagedMediaSource, so VHS would attach to exactly the pipeline that
// wedges. IS_IOS catches iPhone/iPad UAs regardless of browser; the
// Macintosh+touch check catches iPads masquerading as Macs in non-Safari
// browsers (real Macs report 0 touch points).
export function shouldUseNativeHls(): boolean {
  try {
    const browser = (videojs as any).browser
    if (browser?.IS_ANY_SAFARI || browser?.IS_IOS) return true
    return (
      /iPhone|iPad|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
    )
  } catch {
    return false
  }
}

// Guarded diagnostics, shared vocabulary with the surfaces. Enabled with
// ?debug=true; no-op otherwise. The try/catch also covers the node test env,
// where there is no window.
const coreDebug = (() => {
  try {
    return new URLSearchParams(window.location.search).get('debug') === 'true'
  } catch {
    return false
  }
})()
const dbg = (...args: unknown[]) => { if (coreDebug) console.log('[PlaybackCore]', ...args) }

// Clamp a resume target into [0, duration). Garbage in (NaN, negative) resumes
// from the start; an unknown or non-finite duration (live) leaves the target
// alone since there is nothing to clamp against.
export function clampSeekTime(t: number, duration: number): number {
  if (!Number.isFinite(t) || t <= 0) return 0
  if (!Number.isFinite(duration) || duration <= 0) return t
  return Math.min(t, Math.max(0, duration - SEEK_EPSILON))
}

export interface PlaybackCallbacks {
  onPlayStateChange?: (playing: boolean) => void
  onTimeUpdate?: (t: number) => void
  // The end of the last buffered range, in seconds — how far ahead of the
  // playhead bytes have actually arrived. Fired on `progress` and `timeupdate`,
  // because a stream that has finished buffering stops firing `progress` while
  // the playhead keeps moving. Reports 0 when nothing is buffered yet.
  //
  // Read defensively: `buffered` throws on a torn-down element (the same reason
  // mediaDiagnostics.ts:56 guards its own read), so a teardown reports 0 rather
  // than propagating.
  onProgress?: (bufferedEnd: number) => void
  onDurationChange?: (d: number) => void
  onVolumeChange?: (volume: number, muted: boolean) => void
  onBufferingChange?: (buffering: boolean) => void
  onError?: (message: string | null) => void
  onQualityLevelsChange?: (levels: QualityLevelInfo[], activeIdx: number) => void
  // Fired once per setSource() when the element is actually presenting frames
  // of the NEW source — until then it still displays the outgoing stream's
  // last frame, which a freshly mounted surface would happily render.
  onSourcePixels?: () => void
}

// The spatial-audio surface the sphere controller drives. The manager itself
// lives in the core because WebAudio's createMediaElementSource can only ever
// be called once per element — a second call throws, and the element now
// outlives every sphere mount.
export interface SpatialAudioFacade {
  setEnabled(on: boolean): void
  isEnabled(): boolean
  resumeContext(): void
  updateListenerOrientation(yawDegrees: number): void
  setActiveSpeaker(positionDegrees: number | null): void
}

// Decide which media element to drive, given the one we currently hold and the
// one found inside the player. When the tech has substituted its own element
// (iOS — see syncMediaElement) the replacement is adopted, carrying across the
// state that cloneNode does not copy: `muted` and `volume` are IDL properties,
// not reflected attributes, so a clone comes back unmuted at full volume no
// matter what the original was set to. An unmuted element gets its autoplay
// refused on iOS, which is why this transfer is load-bearing rather than tidy.
export function adoptTechElement<T extends { muted: boolean; volume: number }>(
  current: T,
  found: T | null | undefined,
): T {
  if (!found || found === current) return current
  found.muted = current.muted
  found.volume = current.volume
  return found
}

// Whether the media element we hold has to be looked up again. A detached
// element is the signature of the iOS substitution: Html5.disposeMediaElement()
// removes the original from the DOM before the clone takes over. It is also a
// cheap O(1) property read, so the getter can afford to check it every access
// rather than guessing when the tech decides to swap.
export function shouldResyncMediaElement(
  current: { isConnected: boolean },
  disposed: boolean,
): boolean {
  return !disposed && !current.isConnected
}

export interface PlaybackCoreOptions {
  initialTime?: number
  // Start position as a fraction of the duration (0.5 = halfway), resolved
  // once the source reports one. The admin's camera previews open halfway in,
  // past the intro card, on someone's face. initialTime wins when both are set.
  initialTimeFraction?: number
  autoPlay?: boolean
  // Stay muted once ready, where ready() would otherwise unmute.
  muted?: boolean
}

// Owns the one and only media pipeline for a player mount: a single <video>
// element and a single video.js player, created once and destroyed only on
// unmount. Views switch by calling setSource() on this player, so a second
// MediaSource never exists and the teardown race that froze surface swaps is
// structurally impossible.
export class PlaybackCore {
  readonly player: Player

  // The element the tech is ACTUALLY driving. Not necessarily the one we
  // created — see resolveMediaElement().
  private mediaEl: HTMLVideoElement

  // Every surface reads the media element through here (texture source, layout
  // measurements, direct play/seek), so it must never hand back the orphan
  // video.js discarded on iOS.
  //
  // Self-healing rather than resolved once, because the substitution does NOT
  // happen during videojs(): handleSrc_ runs the source through the middleware
  // chain asynchronously and only its callback calls loadTech_(), so the tech —
  // and on iOS its replacement element — is built after the constructor has
  // returned. A detached element is the tell, and isConnected is a cheap
  // property read, so the common path costs nothing.
  get videoElement(): HTMLVideoElement {
    if (shouldResyncMediaElement(this.mediaEl, this.disposed)) this.syncMediaElement('detached')
    return this.mediaEl
  }

  private callbacks: PlaybackCallbacks
  private currentSrc: string
  private currentType: string
  private quality: QualityLevelController | null = null
  private audioManager: SpatialAudioManager | null = null
  // createMediaElementSource throws if called twice on an element, so a failed
  // construction is permanent — never retried.
  private audioManagerFailed = false
  private desiredSpatialAudio = false
  private disposed = false
  // Resume state for the current load (initial or post-setSource); applied
  // once the new source reports a duration.
  private pendingSeek: number | null = null
  private pendingSeekFraction: number | null = null
  private readonly startMuted: boolean
  private pendingPlay = false
  // ?debug=true only — raw element/tech/network instrumentation.
  private diag: DiagnosticsHandle | null = null

  readonly spatialAudio: SpatialAudioFacade = {
    setEnabled: (on: boolean) => {
      this.desiredSpatialAudio = on
      if (on) this.ensureAudioManager()
      this.audioManager?.setEnabled(on)
    },
    isEnabled: () => this.audioManager?.isEnabled() ?? false,
    resumeContext: () => this.audioManager?.resumeContext(),
    updateListenerOrientation: (yawDegrees: number) => this.audioManager?.updateListenerOrientation(yawDegrees),
    setActiveSpeaker: (positionDegrees: number | null) => this.audioManager?.setActiveSpeaker(positionDegrees),
  }

  constructor(
    host: HTMLElement,
    initialSrc: string,
    type: string,
    opts: PlaybackCoreOptions,
    callbacks: PlaybackCallbacks,
  ) {
    this.callbacks = callbacks
    this.currentSrc = initialSrc
    this.currentType = type
    this.pendingSeek = opts.initialTime && opts.initialTime > 0 ? opts.initialTime : null
    this.pendingSeekFraction =
      this.pendingSeek === null && opts.initialTimeFraction && opts.initialTimeFraction > 0
        ? opts.initialTimeFraction
        : null
    this.pendingPlay = !!opts.autoPlay
    this.startMuted = !!opts.muted

    // crossOrigin is required for the sphere's WebGL texture and harmless for
    // flat playback; the element starts muted so an autoplay request is never
    // blocked outright, and ready() unmutes it.
    const el = document.createElement('video')
    el.className = 'video-js'
    el.setAttribute('playsinline', 'true')
    el.setAttribute('webkit-playsinline', 'true')
    el.crossOrigin = 'anonymous'
    el.muted = true
    el.style.width = '100%'
    el.style.height = '100%'
    el.style.objectFit = 'contain'
    host.appendChild(el)
    this.mediaEl = el

    dbg('creating player', {
      src: initialSrc,
      type,
      ...opts,
      nativeHls: shouldUseNativeHls(),
      touchPoints: navigator.maxTouchPoints,
    })

    // Before the player exists, because the very first thing an encrypted
    // stream does is fetch and decrypt its init segment. See the file for the
    // video.js defect this works around and how to tell when it can go.
    installAesInitSegmentIvFix()

    this.player = videojs(el, {
      controls: false,
      autoplay: false,
      preload: 'auto',
      playsinline: true,
      fill: true,
      sources: [{ src: initialSrc, type }],
      html5: shouldUseNativeHls()
        ? {
            // Safari/iOS: WebKit's native HLS stack. See shouldUseNativeHls().
            vhs: { overrideNative: false },
            nativeAudioTracks: true,
            nativeVideoTracks: true,
          }
        : {
            vhs: {
              overrideNative: true,
              // VHS seeds its bandwidth estimate from the FIRST response it
              // sees, which is the ~1.5KB master playlist. Served from cache
              // in under a millisecond that yields estimates in the gigabits
              // — 16 Gbps was observed — and VHS then picks the top rung of
              // the ladder before a single segment has been fetched. Seed it
              // with a realistic figure instead, and don't persist estimates:
              // a poisoned value survives reloads per-origin and is nearly
              // impossible to diagnose from the outside.
              bandwidth: INITIAL_BANDWIDTH_ESTIMATE,
              useBandwidthFromLocalStorage: false,
              // VHS (3.x, on by default) asks Chrome how fast the network is
              // (navigator.connection.downlink) and, unless BOTH that and its
              // own measurement are 10 Mbps or more, uses Chrome's figure
              // INSTEAD of its own. Chrome's figure is a coarse, privacy-
              // rounded guess: 1.55 Mbps on a desktop here, ~1.5 on Gavin's
              // Android (3 Nov) while VHS was measuring 70-95 Mbps. So every
              // Chrome viewer was held near 720p. Off: VHS uses what it
              // actually measures downloading segments.
              useNetworkInformationApi: false,
              // The sphere hosts the element at 1x1px (it is a texture
              // source, not a display surface), so player dimensions must not
              // drive selection or every 360 view would run at 240p.
              limitRenditionByPlayerDimensions: false,
            },
            nativeAudioTracks: false,
            nativeVideoTracks: false,
          },
    })

    // Usually a no-op — the tech is built asynchronously, so on iOS the
    // substitution has not happened yet at this point. Kept because a tech
    // that IS ready synchronously must not be missed; ready() and the
    // videoElement getter cover the normal (async) case.
    this.syncMediaElement('construction')

    this.diag = attachMediaDiagnostics(this.player, this.mediaEl, initialSrc)

    const player = this.player
    const cb = () => this.callbacks
    player.on('playing', () => {
      dbg('event: playing')
      cb().onPlayStateChange?.(true)
      cb().onBufferingChange?.(false)
    })
    player.on('pause', () => cb().onPlayStateChange?.(false))
    player.on('ended', () => cb().onPlayStateChange?.(false))
    // How far the bytes have got. `progress` is the event that means "more data
    // arrived", but it stops once buffering is complete, so `timeupdate` keeps
    // it honest while a fully-buffered stream plays out.
    const reportBufferedEnd = () => {
      let end = 0
      try {
        const el = this.videoElement
        if (el.buffered.length > 0) end = el.buffered.end(el.buffered.length - 1)
      } catch {
        // buffered throws on a torn-down element
      }
      cb().onProgress?.(end)
    }
    player.on('progress', reportBufferedEnd)
    player.on('timeupdate', () => {
      cb().onTimeUpdate?.(player.currentTime() || 0)
      reportBufferedEnd()
    })
    player.on('durationchange', () => cb().onDurationChange?.(player.duration() || 0))
    player.on('volumechange', () => cb().onVolumeChange?.(player.volume() ?? 1, player.muted() ?? false))
    player.on('waiting', () => cb().onBufferingChange?.(true))
    player.on('canplay', () => cb().onBufferingChange?.(false))
    player.on('error', () => {
      dbg('event: error', player.error())
      // video.js raises MEDIA_ERR_SRC_NOT_SUPPORTED from its own source-
      // selection bookkeeping, which can go stale while the media element is
      // perfectly healthy (see the play() comment below). Trust the element:
      // if it has no error of its own and has decoded data, nothing is broken
      // and a "stream failed to load" overlay would be a lie.
      // Read through the getter, never the `el` we created: on iOS that one
      // is the discarded orphan and would make every player error look real.
      const live = this.videoElement
      if (!live.error && live.readyState >= 2 /* HAVE_CURRENT_DATA */) {
        dbg('ignoring player-level error — element is healthy', {
          readyState: live.readyState,
          playerError: player.error()?.code,
        })
        return
      }
      cb().onBufferingChange?.(false)
      const src = this.currentSrc
      diagnoseStreamError(src).then((m) => {
        // A source change (or unmount) may have overtaken the probe — the
        // message would describe a stream nobody is watching any more.
        if (this.disposed || src !== this.currentSrc) return
        cb().onError?.(m)
      })
    })
    // The duration is known here, so a resume target can finally be clamped.
    player.on('loadedmetadata', () => {
      dbg('event: loadedmetadata', { duration: player.duration() })
      cb().onError?.(null)
      this.applyPendingResume(true)
    })
    // ...but on Safari's native HLS `loadedmetadata` arrives with nothing
    // buffered, so the resume above defers its seek. These are the events that
    // announce real decoded data; applyPendingResume clears what it applies,
    // so calling it repeatedly is free.
    player.on('loadeddata', () => this.applyPendingResume(true))
    player.on('canplay', () => this.applyPendingResume(true))

    player.ready(() => {
      // ready() is async — a StrictMode double-mount can dispose us first.
      if (this.disposed || player.isDisposed()) { dbg('ready fired after dispose — skipping'); return }
      // The tech exists by now, so this is the first moment the iOS element
      // substitution can be seen. Everything below (muted, volume) must land
      // on the element that is really playing.
      this.syncMediaElement('ready')
      dbg('ready fired', { readyState: player.readyState(), error: player.error() })
      this.diag?.dump('ready')
      cb().onError?.(null)
      player.muted(this.startMuted)
      player.volume(1)
      cb().onVolumeChange?.(1, this.startMuted)
      // Deliberately NOT resuming here. `ready` fires before the media load
      // algorithm has even started (readyState NOTHING / networkState EMPTY),
      // and calling play() on an element with no source attached wedges the
      // load on Safari — it sits at networkState=LOADING forever, never
      // firing loadedmetadata and never erroring. The 'loadedmetadata'
      // handler above calls applyPendingResume(true) once there is actually
      // media to play, which is the only correct moment for both the seek and
      // the autoplay request.
      // Quality levels are independent of playback — init after, and never let
      // a failure here block play.
      try {
        this.quality = new QualityLevelController(player, (levels, activeIndex) =>
          cb().onQualityLevelsChange?.(levels, activeIndex),
        )
      } catch {
        // qualityLevels plugin unavailable — leave the bitrate menu hidden.
      }
    })
  }

  // Point `mediaEl` at the element the tech is really driving.
  //
  // video.js's Html5 tech sets `movingMediaElementInDOM = !IS_IOS`, and when
  // that is false createEl() does NOT adopt the <video> we handed it. It
  // clones the tag, inserts the clone, and calls Html5.disposeMediaElement()
  // on the original — which removes it from the DOM, strips its src attribute
  // and calls load() on it. ("On the iPhone video will break if you move the
  // element, so we have to create a brand new element.")
  //
  // So on every iPhone and iPad, the element this class created is a detached
  // orphan: forever readyState=NOTHING / networkState=EMPTY / currentSrc=none,
  // while the clone beside it plays perfectly. Anything driving the orphan is
  // driving a corpse — play() rejects with AbortError, videoWidth stays 0 so
  // FlatSurface never lays the quadrant out, and the sphere textures nothing.
  // That is the whole iPhone blank screen.
  //
  // cloneNode(true) copies attributes but not IDL properties, and `muted` is
  // not a reflected attribute (`defaultMuted` is), so the clone also comes
  // back UNMUTED — which would get its autoplay refused on iOS. Restore it
  // via the property rather than the attribute: setting the attribute would
  // make it re-mute on every subsequent load(), silencing view switches.
  private syncMediaElement(why: string): void {
    let found: HTMLVideoElement | null = null
    try {
      found = (this.player.el()?.querySelector('video') as HTMLVideoElement) || null
    } catch {
      found = null
    }
    const previous = this.mediaEl
    this.mediaEl = adoptTechElement(previous, found)
    if (this.mediaEl === previous) return
    // Point the instrumentation at the live element too, or every future log
    // describes the corpse and the next debugging round starts from a lie.
    this.diag?.retarget(this.mediaEl)
    dbg('media element replaced by the tech', {
      why,
      previousConnected: previous.isConnected,
      nextConnected: this.mediaEl.isConnected,
      muted: this.mediaEl.muted,
    })
  }

  // The switch primitive: keep the pipeline, swap the stream. Captures the
  // current position and play state, points the player at the new source, and
  // restores both once the new source reports its duration.
  setSource(src: string, type: string = this.currentType): void {
    if (this.disposed || this.player.isDisposed()) return
    // Views can share a source (no flat fallback configured) — reloading the
    // same stream would drop the buffer for nothing.
    if (src === this.currentSrc) { dbg('setSource: unchanged, skipping', { src }); return }
    const resumeTime = this.player.currentTime() || 0
    const resumePlay = !this.player.paused()
    dbg('setSource', { src, resumeTime, resumePlay })
    this.currentSrc = src
    this.currentType = type
    this.pendingSeek = resumeTime > 0 ? resumeTime : null
    this.pendingPlay = resumePlay
    this.callbacks.onError?.(null)
    this.callbacks.onBufferingChange?.(true)
    this.diag?.dump('before setSource')
    this.player.src({ src, type })
    // A source change can make video.js rebuild the tech, and with it the
    // media element. Cheap to re-check, catastrophic to miss.
    this.syncMediaElement('setSource')
    this.diag?.dump('after setSource')
    this.notifySourcePixels()
  }

  // Resolve when the video element is presenting frames of the source loaded
  // by the most recent setSource(). 'loadeddata' guarantees the element has
  // decoded the new source's first frame (it stops displaying the old one),
  // and where available one requestVideoFrameCallback after that pins the
  // moment a frame is actually composited.
  //
  // Whatever happens, this MUST resolve: the caller paints an opaque black
  // cover over the surface until it does, so a signal that never arrives is a
  // permanently black player. rVFC in particular only fires while frames are
  // being composited, which is not guaranteed for a hidden or paused element.
  // The deadline below turns the worst case into a brief stale frame rather
  // than a dead view.
  private sourceGen = 0
  private notifySourcePixels(): void {
    const gen = ++this.sourceGen
    const el = this.videoElement
    let settled = false
    const done = (why: string) => {
      if (settled || this.disposed || gen !== this.sourceGen) return
      settled = true
      window.clearTimeout(deadline)
      dbg('sourcePixels', { why, gen })
      this.callbacks.onSourcePixels?.()
    }
    const deadline = window.setTimeout(() => done('deadline'), SOURCE_PIXELS_DEADLINE_MS)
    el.addEventListener(
      'loadeddata',
      () => {
        if (settled || this.disposed || gen !== this.sourceGen) return
        const rvfc = (el as HTMLVideoElement & {
          requestVideoFrameCallback?: (cb: () => void) => number
        }).requestVideoFrameCallback?.bind(el)
        if (!rvfc) { done('loadeddata') ; return }
        // rVFC pins the exact moment a frame is composited, which is what the
        // flat view wants. But the sphere hosts this element at opacity 0 (it
        // is a texture source), and a browser under no obligation to composite
        // an invisible video may never call back — so race it against a short
        // grace period rather than trusting it. `loadeddata` already means the
        // new source's first frame is decoded, so uncovering here is safe.
        window.setTimeout(() => done('post-loadeddata grace'), SOURCE_PIXELS_GRACE_MS)
        rvfc(() => done('rvfc'))
      },
      { once: true },
    )
  }

  // Plays the media ELEMENT directly rather than going through
  // player.play(). video.js's play_() gates on
  //
  //   isSrcReady = !changingSrc_ && (this.src() || this.currentSrc())
  //
  // and when VHS attaches MediaSource it fires a `sourceset` carrying the
  // blob: URL. video.js's handleTechSourceset_ can then clear its source
  // cache and wait for the NEXT `loadstart` to repopulate it — but loadstart
  // has already fired by then, so currentSrc() stays empty for good. play()
  // therefore decides the source isn't ready, returns WITHOUT ever calling
  // techGet_('play'), and — on Safari and iOS only — calls this.load(), which
  // re-runs source selection against an empty list and raises
  // MEDIA_ERR_SRC_NOT_SUPPORTED. Chrome skips that load() branch entirely,
  // which is why this was Safari-only.
  //
  // The element itself is fine throughout (readyState HAVE_CURRENT_DATA, real
  // dimensions, buffered data), so playing it directly is both correct and
  // simpler. video.js's UI state stays in sync because it tracks the
  // element's play/pause/playing events, which still fire.
  play(): void {
    if (this.disposed || this.player.isDisposed()) return
    this.spatialAudio.resumeContext()
    this.diag?.dump('before play()')
    const p = this.videoElement.play()
    if (p) {
      // Note: a bare p.then() here would leave its own rejection unhandled —
      // the diagnostics ride on the existing catch instead.
      p.then(() => this.diag?.dump('play() resolved'), () => {})
      p.catch((e) => {
        dbg('play REJECTED, retrying muted', (e as Error)?.name, (e as Error)?.message)
        this.diag?.dump(`play() REJECTED ${(e as Error)?.name}: ${(e as Error)?.message}`)
        // Chrome/Safari reject an unmuted play() detached from a user gesture
        // (e.g. resuming after a probe-triggered view switch). Resume muted
        // rather than staying paused; the volumechange event keeps the mute
        // icon in sync so one click restores sound.
        if (this.disposed || this.player.isDisposed()) return
        this.player.muted(true)
        // player.muted() reaches the element through the tech, which is not
        // guaranteed synchronous — and on iOS the retry only succeeds if the
        // element is ALREADY muted when play() is called. Set it directly;
        // the volumechange event keeps video.js's own state in sync.
        this.videoElement.muted = true
        // Same reasoning as above — the retry must not go through play_() either.
        this.videoElement.play()?.catch((e2) => {
          // Even muted playback can be refused (iOS Low Power Mode blocks all
          // autoplay). The UI is already showing a play button; a user tap
          // carries the gesture that makes the next play() legal.
          dbg('muted retry REJECTED', (e2 as Error)?.name, (e2 as Error)?.message)
          this.diag?.dump(`muted retry REJECTED ${(e2 as Error)?.name}: ${(e2 as Error)?.message}`)
        })
      })
    }
  }

  pause(): void {
    if (this.disposed || this.player.isDisposed()) return
    this.player.pause()
  }

  seek(t: number): void {
    if (this.disposed || this.player.isDisposed()) return
    this.player.currentTime(clampSeekTime(t, this.player.duration() || 0))
  }

  setVolume(v: number): void {
    if (this.disposed || this.player.isDisposed()) return
    this.player.volume(v)
    if (this.player.muted() && v > 0) this.player.muted(false)
    this.audioManager?.setVolume(v)
  }

  toggleMute(): void {
    if (this.disposed || this.player.isDisposed()) return
    const muted = !this.player.muted()
    this.player.muted(muted)
    this.audioManager?.setMuted(muted)
  }

  getCurrentTime(): number {
    if (this.disposed || this.player.isDisposed()) return 0
    return this.player.currentTime() || 0
  }

  getDuration(): number {
    if (this.disposed || this.player.isDisposed()) return 0
    return this.player.duration() || 0
  }

  setQualityLevel(i: number | 'auto'): void {
    this.quality?.setQualityLevel(i)
  }

  // Only on VideoPlayer unmount — never on a view switch.
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    dbg('dispose')
    this.diag?.dump('dispose')
    this.diag?.dispose()
    this.diag = null
    this.quality?.dispose()
    this.quality = null
    try {
      this.audioManager?.dispose()
    } catch {
      // AudioContext already closed
    }
    this.audioManager = null
    if (!this.player.isDisposed()) this.player.dispose()
  }

  // Restore position then play state for the current load. `force` is set once
  // metadata has arrived: before that a duration of 0 means "not known yet" and
  // seeking would be dropped by the browser, so we wait.
  private applyPendingResume(force: boolean): void {
    if (this.disposed || this.player.isDisposed()) return
    if (this.pendingSeekFraction !== null) {
      const duration = this.player.duration() || 0
      if (Number.isFinite(duration) && duration > 0) {
        this.pendingSeek = this.pendingSeekFraction * duration
        this.pendingSeekFraction = null
      } else if (force) {
        this.pendingSeekFraction = null
      }
    }
    if (this.pendingSeek !== null) {
      const duration = this.player.duration() || 0
      if (!force && !(duration > 0)) return
      // Safari's native HLS pipeline hangs when the playhead is moved before
      // the element holds any decoded data: `seeking` fires, `seeked` never
      // does, and the view sits black forever. That path reports
      // `loadedmetadata` with `buffered` still empty (duration known, no
      // frames), which is exactly when a view switch wants to restore its
      // position — so wait for real data. The loadeddata/canplay handlers
      // call back in the moment it lands.
      if (this.videoElement.readyState < 2 /* HAVE_CURRENT_DATA */) {
        dbg('resume: deferring seek until data arrives', {
          readyState: this.videoElement.readyState,
          target: this.pendingSeek,
        })
        return
      }
      const target = clampSeekTime(this.pendingSeek, duration)
      this.pendingSeek = null
      if (target > 0) {
        dbg('resume: seeking', { target, duration })
        this.player.currentTime(target)
      }
    }
    if (this.pendingPlay) {
      this.pendingPlay = false
      this.play()
    }
  }

  private ensureAudioManager(): void {
    if (this.audioManager || this.audioManagerFailed || this.disposed) return
    try {
      const manager = new SpatialAudioManager(this.videoElement)
      manager.setVolume(this.player.volume() ?? 1)
      if (this.player.muted()) manager.setMuted(true)
      if (this.desiredSpatialAudio) manager.setEnabled(true)
      this.audioManager = manager
    } catch (e) {
      // WebAudio unavailable, or this element already has a media-element
      // source. Either way a retry can only throw again.
      this.audioManagerFailed = true
      dbg('spatial audio unavailable', e)
    }
  }
}
